// 評価の説明の「単一ソース」。
// 使い方ページ（/how-to-use）と結果画面カード（FeedbackCompetencyCard）が
// このモジュールを共有し、軸名・社会人基礎力の定義・補足文言が複数画面でズレないようにする。
//
// - 5軸のラベルは score-utils の SCORE_LABELS を再利用する（ラベルの唯一の定義元）。
// - 社会人基礎力の 3能力12要素のグルーピングと要素ラベルは score-utils を再利用し、
//   本モジュールでは各能力の別名・要約と各要素の意味（説明文）だけを新たに定義する。

import {
  SCORE_LABELS,
  COMPETENCY_ABILITIES,
  COMPETENCY_LABELS,
  type ScoreKey,
  type CompetencyKey,
} from "@/lib/score-utils";

// ── 回答を測定する5つの観点（全員共通の土台） ──

export interface ScoreAxisGuide {
  key: ScoreKey;
  label: string;
  /** その軸で何を見るかの一文説明。 */
  description: string;
}

/** 表示順（土台となる5観点）。 */
export const SCORE_AXIS_ORDER: ScoreKey[] = [
  "structure_logic",
  "specificity",
  "motivation",
  "self_awareness",
  "expression",
];

const AXIS_DESCRIPTIONS: Record<ScoreKey, string> = {
  structure_logic: "結論から話し、根拠と具体例が筋道立てて結びついているか。",
  specificity: "数値・固有名詞・行動ベースで、経験が具体的に語られているか。",
  motivation: "なぜこの仕事・会社を選ぶのか、動機が一貫してつながっているか。",
  self_awareness:
    "強み・弱みを客観的に把握し、課題への向き合い方を語れているか。",
  expression: "言葉遣いが適切で、前向きに分かりやすく伝えられているか。",
};

export const SCORE_AXIS_GUIDE: ScoreAxisGuide[] = SCORE_AXIS_ORDER.map((key) => ({
  key,
  label: SCORE_LABELS[key],
  description: AXIS_DESCRIPTIONS[key],
}));

// ── 社会人基礎力（経産省 3能力12要素／解釈の言語・新卒系のみ） ──

export interface CompetencyElementGuide {
  key: CompetencyKey;
  name: string;
  desc: string;
}

export interface CompetencyAbilityGuide {
  /** 能力名（例: 前に踏み出す力）。 */
  name: string;
  /** 通称（例: アクション）。 */
  alias: string;
  /** 能力の要約。 */
  summary: string;
  elements: CompetencyElementGuide[];
}

const ABILITY_META: Record<string, { alias: string; summary: string }> = {
  前に踏み出す力: {
    alias: "アクション",
    summary:
      "指示を待つのではなく、一歩前に踏み出し、失敗しても粘り強く取り組む力",
  },
  考え抜く力: {
    alias: "シンキング",
    summary: "疑問を持ち、答えのない課題に対して自分の頭で考え抜く力",
  },
  チームで働く力: {
    alias: "チームワーク",
    summary:
      "立場や意見の異なる多様な人と、共通の目標に向けて協力していく力",
  },
};

const ELEMENT_DESCRIPTIONS: Record<CompetencyKey, string> = {
  initiative: "物事に進んで取り組む力",
  influence: "他人に働きかけ巻き込む力",
  execution: "目的を設定し確実に行動する力",
  problem_finding: "現状を分析し目的や課題を明らかにする力",
  planning: "課題の解決に向けたプロセスを明らかにし準備する力",
  creativity: "新しい価値を生み出す力",
  transmission: "自分の意見をわかりやすく伝える力",
  listening: "相手の意見を丁寧に聴く力",
  flexibility: "意見の違いや立場の違いを理解する力",
  situational_awareness: "自分と周囲の人々や物事との関係性を理解する力",
  discipline: "社会のルールや人との約束を守る力",
  stress_control: "ストレスの発生源に対応する力",
};

/**
 * 3能力とその配下12要素の定義データ。
 * グルーピングと要素ラベルは score-utils を単一ソースとして参照し、
 * 別名・要約・要素の意味は本モジュールで補う。
 */
export const COMPETENCY_ABILITY_GROUPS: CompetencyAbilityGuide[] =
  COMPETENCY_ABILITIES.map((ability) => ({
    name: ability.label,
    alias: ABILITY_META[ability.label].alias,
    summary: ABILITY_META[ability.label].summary,
    elements: ability.elements.map((key) => ({
      key,
      name: COMPETENCY_LABELS[key],
      desc: ELEMENT_DESCRIPTIONS[key],
    })),
  }));

// ── 補足文言（合否予測ではない旨・経歴最適化の案内） ──

/** 社会人基礎力の定義（1文）。 */
export const COMPETENCY_DEFINITION =
  "「社会人基礎力」とは、経済産業省が提唱した「職場や地域社会で多様な人々と仕事をしていくために必要な基礎的な力」です。";

/** スコアは合否予測ではない旨の注意書き。 */
export const EVALUATION_SCORE_DISCLAIMER =
  "スコアやランクは、あなたの回答を振り返るための目安です。合否を予測するものではありません。点数に一喜一憂せず、改善ポイントを一つずつ克服していきましょう。";

/** 各画面の案内・使い方ページで共通して使う「経歴に応じた最適化」の補足。 */
export const CAREER_STAGE_OPTIMIZATION_NOTICE =
  "質問と評価の観点は、あなたの練習タイプ（就活／転職）に合わせて自動で最適化されます。";

/** 使い方ページへの導線に使うパスとラベル。 */
export const HOW_TO_USE_PATH = "/how-to-use";
export const HOW_TO_USE_LABEL = "MENREN の使い方";

/** 公開トップの各セクションから使い方ページへ誘導するリンクのラベル。 */
export const HOW_TO_USE_DETAIL_LABEL = "使い方をくわしく見る";
